import {useAppDispatch, useAppSelector} from "../../../app/store";
import {
    changeLinkModal,
    closeModal, deleteLink,
    EditLinkPayload,
    getLink, NewLinkPayload,
    saveEditLink, saveNewLink
} from "../../../app/slices/modals/linkModal";
import AdminLoader from "../AdminLoader";
import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Alert, Button, Col, Form, FormControl, InputGroup, Modal} from "react-bootstrap";
import {ADMIN_LANG} from "../../../settings";
import AsyncGameSelect from "../gameView/AsyncGameSelect";
import {PlainDetails} from "../../../types/admin";
import DatePicker from "react-datepicker";
import {getFormattedDate} from "../utils";
import {format} from "date-fns";
import {fi} from "date-fns/locale";
import Confirm from "../Confirm";
import {isFulfilled} from "@reduxjs/toolkit";
import {useNavigate} from "react-router-dom";
import {Clipboard} from "react-bootstrap-icons";
import toast from "react-hot-toast";
import AsyncTeamsSelect from "../gameView/AsyncTeamsSelect";


const LinkModal = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const {
        isOpen, loading, description, game, selected_teams,
        error, link_id, max_usages, expires, is_anonymous, delete_message,
        url, team_options, game_options
    } = useAppSelector(state => state.linkModal);

    const [showConfirmDelete, setShowConfirmDelete] = useState(false);


    useEffect(() => {
        if (link_id) {
            dispatch(getLink(link_id));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [link_id]);

    const handleSave = (e: React.FormEvent) => {
        e.preventDefault();

        const payload = {
            description,
            game,
            selected_teams,
            max_usages,
            expires,
            is_anonymous,
            ...(link_id && {link_id})
        };

        const action = link_id ? saveEditLink(payload as EditLinkPayload) : saveNewLink(payload as NewLinkPayload);

        dispatch(action).then((resultAction) => {
            if (!link_id && isFulfilled(resultAction)) {
                let newId = '0';
                if (resultAction.payload && resultAction.payload.link_id) {
                    newId = resultAction.payload.link_id;
                }
                navigate(`/admin/links/?new=${newId}`);
            }
        });
    };

    const handleDelete = () => {
        setShowConfirmDelete(true)
    };

    const handleConfirm = () => {
        setShowConfirmDelete(false);
        if (link_id) {
            dispatch(deleteLink(link_id));
        }
    };

    const handleGameChange = (value: PlainDetails) => {
        dispatch(changeLinkModal({game: value}));
    }

    const handleTeamsChange = (value: PlainDetails[]) => {
        dispatch(changeLinkModal({selected_teams: value}));
    }

    const handleDescriptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        dispatch(changeLinkModal({description: e.target.value}));
    }

    const handleUseAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        dispatch(changeLinkModal({max_usages: parseInt(e.target.value)}));
    }

    const handleDateChange = (date: Date | null) => {
        if (date) {
            const _formattedDate = format(date, "yyyy-MM-dd");
            dispatch(changeLinkModal({expires: _formattedDate}));
        } else {
            dispatch(changeLinkModal({expires: ''}));
        }
    };
    const _endDate = getFormattedDate(expires);

    const handleCopyUrl = useCallback(() => {
        if (url) {
            navigator.clipboard.writeText(url)
                .then(() => toast.success(ADMIN_LANG.url_copied, {position: 'top-right'}))
                .catch(() => toast.error(ADMIN_LANG.url_copy_failed, {position: 'top-right'}));
        }
    }, [url]);

    const linkTitle = useMemo(() => {
        return link_id ? ADMIN_LANG.edit_link : ADMIN_LANG.new_link;
    }, [link_id]);

    return (
        <Modal size='xl' show={isOpen} onHide={() => dispatch(closeModal())} className='admin-modal'>
            {loading && <AdminLoader/>}
            <Modal.Header closeButton>
                <Modal.Title>{linkTitle}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error && (
                    <Alert variant='danger' dismissible>
                        <div dangerouslySetInnerHTML={{__html: error}}/>
                    </Alert>
                )}
                <Form id='link-modal-form' className='row gy-3' onSubmit={handleSave}>
                    {!link_id ? (
                        <Col xs={12}>
                            <Form.Group controlId='description'>
                                <Form.Label>{ADMIN_LANG.description_emails}</Form.Label>
                                <Form.Control as='textarea' onChange={handleDescriptionChange} required
                                              value={description}/>
                                <Form.Text className='help-text'>Syötä yksi tai useampi sähköpostiosoite, joista luodaan
                                    kutsulinkit.<br></br> Erota sähköpostiosoitteet rivinvaihdolla tai
                                    pilkulla. <br></br>Jokaiselle
                                    osoitteelle luodaan oma kutsulinkki.</Form.Text>
                            </Form.Group>
                        </Col>
                    ) : (
                        <Col xs={12}>
                            <Form.Group controlId='description'>
                                <Form.Label>{ADMIN_LANG.description}</Form.Label>
                                <Form.Control type='text' onChange={handleDescriptionChange} required
                                              value={description}/>
                            </Form.Group>
                        </Col>
                    )
                    }
                    <Col xs={12}>
                        <Form.Group controlId='is_anonymous'>
                            <Form.Switch label={ADMIN_LANG.is_anonymous}
                                         className='form-label mb-0'
                                         checked={is_anonymous}
                                         onChange={() => dispatch(changeLinkModal({is_anonymous: !is_anonymous}))}/>
                            <Form.Text className='help-text'>Valitse tämä, jos haluat, että kutsulinkit
                                luodaan anonyymeiksi.<br></br> Anonyyminä kutsutun tietoja ei tallenneta.</Form.Text>
                        </Form.Group>
                    </Col>
                    <Col xs={12} md={6}>
                        <Form.Group controlId='game'>
                            <Form.Label>{ADMIN_LANG.game}</Form.Label>
                            <AsyncGameSelect defaultOptions={game_options} initialValue={game} onChange={handleGameChange}/>
                        </Form.Group>
                    </Col>
                    <Col xs={12} md={6}>
                        <Form.Group controlId='team'>
                            <Form.Label>{ADMIN_LANG.team}</Form.Label>
                            <AsyncTeamsSelect selectedTeams={selected_teams} initialValues={team_options} onChange={handleTeamsChange}/>
                        </Form.Group>
                    </Col>
                    <Col xs={12} md={6}>
                        <Form.Group controlId='max_usages'>
                            <Form.Label>{ADMIN_LANG.max_usages}</Form.Label>
                            <Form.Control onChange={handleUseAmountChange} value={max_usages} type='number' min={0}/>
                            <Form.Text className='help-text'>Määritä, kuinka monta kertaa kutsulinkkiä voi
                                käyttää. <br></br>0 tarkoittaa, että linkkiä voi käyttää rajattomasti.</Form.Text>
                        </Form.Group>
                    </Col>
                    <Col xs={12} md={6}>
                        <Form.Group controlId='date_end'>
                            <Form.Label>{ADMIN_LANG.use_end}</Form.Label>
                            <DatePicker
                                wrapperClassName='d-block'
                                isClearable
                                selected={_endDate}
                                minDate={new Date()}
                                dateFormat="dd.MM.yyyy"
                                className="form-control"
                                placeholderText={ADMIN_LANG.use_end}
                                onChange={(date) => handleDateChange(date)}
                                locale={fi}
                            />
                            <Form.Text className='help-text'>Valitse päivämäärä, jonka jälkeen kutsulinkkiä ei voi enää
                                käyttää. <br></br>Valitun päivämäärän jälkeen linkki vanhenee ja muuttuu
                                käyttökelvottomaksi. </Form.Text>
                        </Form.Group>
                    </Col>
                    {url && (
                        <Col xs={12}>
                            <Form.Label>{ADMIN_LANG.copy_team_url}</Form.Label>
                            <InputGroup>
                                <FormControl type="text" value={url} readOnly/>
                                <Button variant="success" onClick={handleCopyUrl}>
                                    <Clipboard/><span className='ms-2'>{ADMIN_LANG.copy_url}</span>
                                </Button>
                            </InputGroup>
                        </Col>
                    )}
                </Form>
            </Modal.Body>
            <Modal.Footer>
                {link_id && (
                    <Button variant='danger' className='me-auto' onClick={handleDelete}>
                        {ADMIN_LANG.delete}
                    </Button>
                )}
                <Button variant='outline-primary' onClick={() => dispatch(closeModal())}>
                    {ADMIN_LANG.close}
                </Button>
                <Button form='link-modal-form' type='submit' variant='primary'>
                    {ADMIN_LANG.save}
                </Button>

            </Modal.Footer>
            <Confirm
                show={showConfirmDelete}
                message={delete_message}
                onConfirm={handleConfirm}
                onCancel={() => setShowConfirmDelete(false)}
            />
        </Modal>
    )
}

export default LinkModal;